/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Tag,
  Tooltip,
  Modal,
  Form,
  Input,
  Button,
  Menu,
  Dropdown,
  Image,
  message,
} from "antd";
import {
  EditOutlined,
  PlayCircleFilled,
  SaveOutlined,
  LoadingOutlined,
  EllipsisOutlined,
  NotificationOutlined,
  PlusOutlined,
  DownloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Storage } from "aws-amplify";
import MediaFetcher from "../library/MediaFetcher";
import CreativePreviewModal from "../../OrgConfig/components/CreativePreviewModal";
import CreativePagination from "./CreativePagination";
import { modal as modalTokens } from "../../Tasks/configs/designTokens";
import moment from "moment";

/**
 * Desktop table view for Creatives — 4 column variants matching the original
 * OrgConfig tables: Display Units, Video Units, Display Groups, Video Groups.
 */

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDuration = (seconds) => {
  if (!seconds) return "—";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// Fetches signed URL from S3 (shared by image + video thumbnails)
const useSignedUrl = (mediaKey) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!mediaKey) return;
    let mounted = true;
    Storage.get(mediaKey, { expires: 600000 }).then((signedUrl) => {
      if (mounted) setUrl(signedUrl);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [mediaKey]);
  return url;
};

// Image thumbnail with AntD lightbox preview
const PreviewableThumbnail = ({ mediaKey }) => {
  const url = useSignedUrl(mediaKey);
  if (!url) return <div className="creative-table__thumbnail-placeholder">?</div>;

  return (
    <Image
      src={url}
      alt="Preview"
      width="100%"
      height="100%"
      className="creative-table__image-cover"
      preview={{
        mask: <EyeOutlined className="creative-table__preview-icon" />,
      }}
    />
  );
};

// Video thumbnail with modal player — whole thumbnail is clickable
const PreviewableVideoThumbnail = ({ mediaKey }) => {
  const url = useSignedUrl(mediaKey);
  const [modalOpen, setModalOpen] = useState(false);

  if (!url) return <div className="creative-table__thumbnail-placeholder">?</div>;

  return (
    <>
      <div
        className="creative-table__video-overlay"
        onClick={() => setModalOpen(true)}
      >
        <video
          src={`${url}#t=0.1`}
          muted
          preload="metadata"
          className="creative-table__video-fill"
        />
        <div className="creative-table__video-badge creative-table__video-badge--clickable">
          <PlayCircleFilled />
        </div>
      </div>
      <Modal
        open={modalOpen}
        footer={null}
        closable={false}
        centered
        width={modalTokens.lg}
        destroyOnClose
        onCancel={() => setModalOpen(false)}
        className="creative-video-preview-modal"
        maskClosable
      >
        <video
          src={url}
          controls
          autoPlay
          onClick={(e) => e.stopPropagation()}
        />
      </Modal>
    </>
  );
};

// Gallery-sized video cell — shows video first-frame + play badge, click opens player modal
const GalleryVideoCell = ({ mediaKey }) => {
  const url = useSignedUrl(mediaKey);
  const [modalOpen, setModalOpen] = useState(false);

  if (!url) return null;

  return (
    <>
      <div
        className="creative-table__gallery-video"
        onClick={(e) => {
          e.stopPropagation();
          setModalOpen(true);
        }}
      >
        <video
          src={`${url}#t=0.1`}
          muted
          preload="metadata"
          className="creative-table__video-fill"
        />
        <div className="creative-table__gallery-play">
          <PlayCircleFilled />
        </div>
      </div>
      <Modal
        open={modalOpen}
        footer={null}
        closable={false}
        centered
        width={modalTokens.lg}
        destroyOnClose
        onCancel={() => setModalOpen(false)}
        className="creative-video-preview-modal"
        maskClosable
      >
        <video
          src={url}
          controls
          autoPlay
          onClick={(e) => e.stopPropagation()}
        />
      </Modal>
    </>
  );
};

// ─── Column Definitions ──────────────────────────────────────────────────────

const getDisplayUnitColumns = ({ onClick, onEdit, hasEditPermission, dropdownOpenRef }) => {
  const cols = [
    {
      title: "",
      key: "image",
      width: 60,
      render: (_, record) => (
        <div
          className="creative-table__thumbnail creative-table__thumbnail--previewable"
          onClick={(e) => e.stopPropagation()}
        >
          <PreviewableThumbnail mediaKey={record.key} />
        </div>
      ),
    },
    {
      title: "Display Ad Unit Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (text, record) => (
        <div
          className="creative-table__name-with-type"
          onClick={(e) => {
            e.stopPropagation();
            onClick(record);
          }}
        >
          <div className="creative-table__name-content">
            {text && text.length > 45 ? (
              <Tooltip title={text}>
                <span>{text.slice(0, 46) + "..."}</span>
              </Tooltip>
            ) : (
              <span>{text || "Untitled"}</span>
            )}
          </div>
          {record.fileType && (
            <span className="creative-table__file-type-pill">
              {record.fileType}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Aspect",
      key: "aspect",
      width: 100,
      sorter: (a, b) =>
        (a.width || 0) - (b.width || 0) || (a.height || 0) - (b.height || 0),
      render: (_, record) =>
        record.width && record.height ? (
          `${record.width}x${record.height}`
        ) : (
          "—"
        ),
    },
    {
      title: "Date Uploaded",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      sorter: (a, b) =>
        moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf(),
      render: (d) =>
        d ? (
          moment(d).format("MM/DD/YYYY hh:mm A")
        ) : (
          "—"
        ),
    },
  ];

  if (hasEditPermission) {
    cols.push({
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => {
        const menu = (
          <Menu onClick={({ key, domEvent }) => {
            domEvent.stopPropagation();
            if (key === "edit") onEdit(record);
          }}>
            <Menu.Item key="edit" icon={<EditOutlined />}>
              Edit
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={menu} trigger={["click"]} overlayClassName="creative-table__actions-menu" onVisibleChange={(open) => { dropdownOpenRef.current = open; }}>
            <Button
              className="creative-table__actions-btn"
              size="small"
              onClick={(e) => e.stopPropagation()}
            >
              <EllipsisOutlined className="creative-table__ellipsis-icon" />
            </Button>
          </Dropdown>
        );
      },
    });
  }

  return cols;
};

const getVideoUnitColumns = ({
  onClick,
  onEdit,
  hasEditPermission,
  videoDurations,
  dropdownOpenRef,
}) => {
  const cols = [
    {
      title: "",
      key: "image",
      width: 60,
      render: (_, record) => (
        <div
          className="creative-table__thumbnail creative-table__thumbnail--previewable"
          onClick={(e) => e.stopPropagation()}
        >
          <PreviewableVideoThumbnail mediaKey={record.key} />
        </div>
      ),
    },
    {
      title: "Video Ad Unit Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (text, record) => {
        if (!text) return <div />;
        return (
          <div
            className="creative-table__name-with-type"
            onClick={(e) => {
              e.stopPropagation();
              onClick(record);
            }}
          >
            <div className="creative-table__name-content">
              {text.length > 45 ? (
                <Tooltip title={text}>
                  <span>{text.slice(0, 46) + "..."}</span>
                </Tooltip>
              ) : (
                <span>{text}</span>
              )}
            </div>
            {record.fileType && (
              <span className="creative-table__file-type-pill">
                {record.fileType}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Aspect",
      key: "aspect",
      width: 100,
      sorter: (a, b) =>
        (a.width || 0) - (b.width || 0) || (a.height || 0) - (b.height || 0),
      render: (_, record) =>
        record.width && record.height ? (
          `${record.width}x${record.height}`
        ) : (
          "—"
        ),
    },
    {
      title: "Duration",
      dataIndex: "key",
      key: "duration",
      width: 100,
      sorter: (a, b) =>
        (videoDurations[a.key] || 0) - (videoDurations[b.key] || 0),
      render: (mediaKey) => (
        formatDuration(videoDurations[mediaKey])
      ),
    },
    {
      title: "Date Uploaded",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      sorter: (a, b) =>
        moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf(),
      render: (d) =>
        d ? (
          moment(d).format("MM/DD/YYYY hh:mm A")
        ) : (
          "—"
        ),
    },
  ];

  if (hasEditPermission) {
    cols.push({
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => {
        const menu = (
          <Menu onClick={({ key, domEvent }) => {
            domEvent.stopPropagation();
            if (key === "edit") onEdit(record);
          }}>
            <Menu.Item key="edit" icon={<EditOutlined />}>
              Edit
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={menu} trigger={["click"]} overlayClassName="creative-table__actions-menu" onVisibleChange={(open) => { dropdownOpenRef.current = open; }}>
            <Button
              className="creative-table__actions-btn"
              size="small"
              onClick={(e) => e.stopPropagation()}
            >
              <EllipsisOutlined className="creative-table__ellipsis-icon" />
            </Button>
          </Dropdown>
        );
      },
    });
  }

  return cols;
};

const getDisplayGroupColumns = ({ onClick, amplify, onGroupAction, onGalleryClick, dropdownOpenRef }) => [
  {
    title: "Display Ad Group Name",
    dataIndex: "name",
    key: "id",
    width: 200,
    ellipsis: true,
    sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    render: (name, record) => (
      <>
        <div
          className="creative-table__name-cell"
          onClick={(e) => {
            e.stopPropagation();
            onClick(record);
          }}
        >
          {name && name.length > 30 ? (
            <Tooltip title={name}>
              <span>{name.slice(0, 30) + "..."}</span>
            </Tooltip>
          ) : (
            <span>{name || "Untitled"}</span>
          )}
        </div>
        {record.isAmplifyDefault && amplify && (
          <Tag color="green">Amplify Default</Tag>
        )}
      </>
    ),
  },
  {
    title: "Ad Units",
    key: "adUnits",
    width: 100,
    render: (_, record) => (record.medias ? record.medias.length : 0),
  },
  {
    title: "Gallery",
    dataIndex: "medias",
    key: "medias",
    width: 300,
    render: (medias) => {
      const uniqueMedias = Array.isArray(medias)
        ? Array.from(
            new Map(medias.map((m) => [m.id || m.key, m])).values()
          )
        : [];
      if (!uniqueMedias.length) return <div>No images</div>;
      const maxVisible = 4;
      const visible = uniqueMedias.slice(0, maxVisible);
      const remaining = uniqueMedias.length - maxVisible;
      return (
        <div className="creative-table__gallery" onClick={(e) => e.stopPropagation()}>
          {visible.map((m) => (
            <div
              key={m.id || m.key}
              className="creative-table__gallery-cell creative-table__gallery-cell--clickable"
              onClick={() => onGalleryClick(m, uniqueMedias)}
            >
              {m.key && (
                <MediaFetcher
                  mediaKey={m.key}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                />
              )}
            </div>
          ))}
          {remaining > 0 && (
            <div
              className="creative-table__gallery-cell creative-table__gallery-more"
              onClick={() => onGalleryClick(uniqueMedias[0], uniqueMedias)}
            >
              +{remaining}
            </div>
          )}
        </div>
      );
    },
  },
  {
    title: "Last Updated",
    dataIndex: "updatedAt",
    key: "updatedAt",
    width: 180,
    defaultSortOrder: "descend",
    sorter: (a, b) =>
      moment(a.updatedAt).valueOf() - moment(b.updatedAt).valueOf(),
    render: (d) =>
      d ? (
        moment(d).format("MM/DD/YYYY hh:mm A")
      ) : (
        "—"
      ),
  },
  {
    title: "Actions",
    key: "actions",
    width: 80,
    align: "center",
    render: (_, record) => {
      const menu = (
        <Menu onClick={({ key, domEvent }) => {
          domEvent.stopPropagation();
          onGroupAction(key, record);
        }}>
          {amplify && (
            <Menu.Item
              key="amplify"
              icon={<NotificationOutlined />}
              disabled={record.isAmplifyDefault}
            >
              Set as Amplify Default
            </Menu.Item>
          )}
          <Menu.Item key="edit" icon={<EditOutlined />}>
            Edit
          </Menu.Item>
          <Menu.Item key="addCreatives" icon={<PlusOutlined />}>
            Add Creatives
          </Menu.Item>
          <Menu.Item key="download" icon={<DownloadOutlined />}>
            Download Creatives
          </Menu.Item>
        </Menu>
      );
      return (
        <Dropdown overlay={menu} trigger={["click"]} overlayClassName="creative-table__actions-menu" onVisibleChange={(open) => { dropdownOpenRef.current = open; }}>
          <Button
            className="creative-table__actions-btn"
            size="small"
            onClick={(e) => e.stopPropagation()}
          >
            <EllipsisOutlined className="creative-table__ellipsis-icon" />
          </Button>
        </Dropdown>
      );
    },
  },
];

const getVideoGroupColumns = ({ onClick, amplify, onGroupAction, onGalleryClick, dropdownOpenRef }) => [
  {
    title: "Video Ad Group Name",
    dataIndex: "name",
    key: "id",
    width: 200,
    ellipsis: true,
    sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    render: (name, record) => (
      <>
        <div
          className="creative-table__name-cell"
          onClick={(e) => {
            e.stopPropagation();
            onClick(record);
          }}
        >
          {name && name.length > 30 ? (
            <Tooltip title={name}>
              <span>{name.slice(0, 30) + "..."}</span>
            </Tooltip>
          ) : (
            <span>{name || "Untitled"}</span>
          )}
        </div>
        {record.isAmplifyDefault && amplify && (
          <Tag color="green">Amplify Default</Tag>
        )}
      </>
    ),
  },
  {
    title: "Ad Units",
    key: "adUnits",
    width: 100,
    render: (_, record) => (record.medias ? record.medias.length : 0),
  },
  {
    title: "Gallery",
    dataIndex: "medias",
    key: "medias",
    width: 300,
    render: (medias) => {
      const uniqueMedias = Array.isArray(medias)
        ? Array.from(
            new Map(medias.map((m) => [m.id || m.key, m])).values()
          )
        : [];
      if (!uniqueMedias.length) return <div>No videos</div>;
      const maxVisible = 4;
      const visible = uniqueMedias.slice(0, maxVisible);
      const remaining = uniqueMedias.length - maxVisible;
      return (
        <div className="creative-table__gallery" onClick={(e) => e.stopPropagation()}>
          {visible.map((m) => (
            <div
              key={m.id || m.key}
              className="creative-table__gallery-cell creative-table__gallery-cell--clickable"
            >
              {m.key && <GalleryVideoCell mediaKey={m.key} />}
            </div>
          ))}
          {remaining > 0 && (
            <div
              className="creative-table__gallery-cell creative-table__gallery-more"
              onClick={() => onGalleryClick(uniqueMedias[0], uniqueMedias)}
            >
              +{remaining}
            </div>
          )}
        </div>
      );
    },
  },
  {
    title: "Last Updated",
    dataIndex: "updatedAt",
    key: "updatedAt",
    width: 180,
    defaultSortOrder: "descend",
    sorter: (a, b) =>
      moment(a.updatedAt).valueOf() - moment(b.updatedAt).valueOf(),
    render: (d) =>
      d ? (
        moment(d).format("MM/DD/YYYY hh:mm A")
      ) : (
        "—"
      ),
  },
  {
    title: "Actions",
    key: "actions",
    width: 80,
    align: "center",
    render: (_, record) => {
      const menu = (
        <Menu onClick={({ key, domEvent }) => {
          domEvent.stopPropagation();
          onGroupAction(key, record);
        }}>
          <Menu.Item key="edit" icon={<EditOutlined />}>
            Edit
          </Menu.Item>
          <Menu.Item key="addCreatives" icon={<PlusOutlined />}>
            Add Creatives
          </Menu.Item>
          <Menu.Item key="download" icon={<DownloadOutlined />}>
            Download Creatives
          </Menu.Item>
        </Menu>
      );
      return (
        <Dropdown overlay={menu} trigger={["click"]} overlayClassName="creative-table__actions-menu" onVisibleChange={(open) => { dropdownOpenRef.current = open; }}>
          <Button
            className="creative-table__actions-btn"
            size="small"
            onClick={(e) => e.stopPropagation()}
          >
            <EllipsisOutlined className="creative-table__ellipsis-icon" />
          </Button>
        </Dropdown>
      );
    },
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────

const CreativeTable = ({
  items,
  isAdUnits,
  isVideo,
  selectedIds,
  setSelectedIds,
  onClick,
  loading,
  pageNumber,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  updateOrgMedia,
  amplify,
  onGroupAction,
  allMedias,
}) => {
  // Flag to suppress row click when actions dropdown is open
  const dropdownOpenRef = useRef(false);

  // Gallery preview state (groups)
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewStartIndex, setPreviewStartIndex] = useState(0);

  const handleGalleryClick = (clickedMedia, allMedias) => {
    const index = allMedias.findIndex((m) => m.key === clickedMedia.key);
    setPreviewImages(allMedias);
    setPreviewStartIndex(index >= 0 ? index : 0);
    setPreviewVisible(true);
  };

  // Edit modal state (units only)
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [editName, setEditName] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [form] = Form.useForm();
  const editingMediaRef = useRef(null);

  // Video duration detection (video units only)
  const [videoDurations, setVideoDurations] = useState({});

  // Duration detection effect
  useEffect(() => {
    if (!isAdUnits || !isVideo || !items || items.length === 0) return;

    const isMountedRef = { current: true };

    const detectDurations = async () => {
      const keysToDetect = items
        .filter((m) => m.key && !videoDurations[m.key])
        .map((m) => m.key)
        .slice(0, 10);

      for (const mediaKey of keysToDetect) {
        if (!isMountedRef.current) break;
        try {
          const videoUrl = await Storage.get(mediaKey, { expires: 600000 });
          if (!isMountedRef.current) break;

          const duration = await new Promise((resolve) => {
            const video = document.createElement("video");
            video.preload = "metadata";

            const cleanup = () => {
              video.onloadedmetadata = null;
              video.onerror = null;
              video.src = "";
              video.load();
            };

            video.onloadedmetadata = () => {
              const dur = Math.round(video.duration);
              cleanup();
              resolve(dur);
            };

            video.onerror = () => {
              cleanup();
              resolve(null);
            };

            video.src = videoUrl;
          });

          if (duration && isMountedRef.current) {
            setVideoDurations((prev) => ({ ...prev, [mediaKey]: duration }));
          }
        } catch {
          // Silently fail for individual durations
        }
      }
    };

    detectDurations();
    return () => {
      isMountedRef.current = false;
    };
  }, [isAdUnits, isVideo, items, videoDurations]);

  // Edit handlers
  const handleEdit = (record) => {
    editingMediaRef.current = record;
    setEditingMedia(record);
    setEditName(record.name || "");
    form.resetFields();
    form.setFields([
      { name: "name", value: record.name || "", touched: false, errors: [] },
    ]);
    setEditModalVisible(true);
  };

  const validateEditName = (_, value) => {
    if (!value) return Promise.resolve();
    const editing = editingMediaRef.current;
    if (!editing) return Promise.resolve();
    const lower = value.toLowerCase().trim();
    if (lower === editing.name.toLowerCase().trim()) return Promise.resolve();
    const mediaList = allMedias || [];
    const isDup = mediaList.some(
      (m) => m.name?.toLowerCase().trim() === lower && m.id !== editing.id
    );
    if (isDup) return Promise.reject(new Error("Creative name already exists"));
    return Promise.resolve();
  };

  const handleEditSubmit = async () => {
    if (!editingMedia || editName === editingMedia.name) return;
    try {
      setEditSubmitting(true);
      message.loading("Action in progress...", 0);
      const capitalized =
        editName.charAt(0).toUpperCase() + editName.slice(1);
      await updateOrgMedia({
        variables: {
          creativeID: editingMedia.id,
          newUrl: editingMedia.url,
          newName: capitalized,
        },
      });
      form.resetFields();
      message.destroy();
      message.success("Successfully updated the creative name");
      setEditModalVisible(false);
      setEditingMedia(null);
      editingMediaRef.current = null;
    } catch {
      message.destroy();
      message.error("Error updating creative name");
    } finally {
      setEditSubmitting(false);
    }
  };

  // Column selection
  let columns;
  if (isAdUnits && !isVideo) {
    columns = getDisplayUnitColumns({
      onClick,
      onEdit: handleEdit,
      hasEditPermission: !!updateOrgMedia,
      dropdownOpenRef,
    });
  } else if (isAdUnits && isVideo) {
    columns = getVideoUnitColumns({
      onClick,
      onEdit: handleEdit,
      hasEditPermission: !!updateOrgMedia,
      videoDurations,
      dropdownOpenRef,
    });
  } else if (!isAdUnits && !isVideo) {
    columns = getDisplayGroupColumns({
      onClick,
      amplify,
      onGroupAction: onGroupAction || (() => {}),
      onGalleryClick: handleGalleryClick,
      dropdownOpenRef,
    });
  } else {
    columns = getVideoGroupColumns({
      onClick,
      amplify,
      onGroupAction: onGroupAction || (() => {}),
      onGalleryClick: handleGalleryClick,
      dropdownOpenRef,
    });
  }

  return (
    <div className="creative-table">
      <Table
        columns={columns}
        dataSource={items}
        rowKey="id"
        loading={loading}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (newKeys) => setSelectedIds(newKeys),
          columnWidth: 32,
        }}
        pagination={false}
        onRow={(record) => ({
          onClick: (e) => {
            if (dropdownOpenRef.current) return;
            if (e.target.closest('.creative-table__actions-btn')) return;
            onClick(record);
          },
          className: selectedIds.includes(record.id)
            ? "creative-table__row--selected"
            : "",
        })}
        tableLayout="fixed"
        scroll={{ x: columns.reduce((sum, col) => sum + (col.width || 200), 32) }}
        size="middle"
      />

      {/* Full pagination bar */}
      {total > 0 && (
        <CreativePagination
          current={pageNumber}
          pageSize={pageSize}
          total={total}
          onChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}

      {/* Edit Media Modal (units only) */}
      {isAdUnits && (
        <Modal
          title="Edit Media"
          open={editModalVisible}
          centered
          footer={null}
          className="creative-edit-modal"
          onCancel={() => {
            setEditModalVisible(false);
            setEditingMedia(null);
            editingMediaRef.current = null;
            form.resetFields();
          }}
        >
          <Form layout="vertical" onFinish={handleEditSubmit} form={form}>
            {editingMedia?.key && !isVideo && (
              <div className="creative-edit-modal__preview">
                <MediaFetcher
                  mediaKey={editingMedia.key}
                  className="creative-edit-modal__preview-media"
                />
              </div>
            )}
            <Form.Item
              label="Name (Max 255 characters)"
              name="name"
              validateTrigger="onBlur"
              rules={[
                { required: true, message: "Please enter a creative name" },
                {
                  max: 255,
                  message: "Creative name must not exceed 255 characters",
                },
                { validator: validateEditName },
              ]}
            >
              <Input
                placeholder="Name of Creative"
                maxLength={255}
                showCount
                onChange={(e) => {
                  const val =
                    e.target.value.charAt(0).toUpperCase() +
                    e.target.value.slice(1);
                  setEditName(val);
                }}
              />
            </Form.Item>
            <div className="creative-edit-modal__actions">
              <Button
                disabled={editName === editingMedia?.name}
                icon={
                  editSubmitting ? <LoadingOutlined /> : <SaveOutlined />
                }
                htmlType="submit"
                type="primary"
              >
                Update
              </Button>
            </div>
          </Form>
        </Modal>
      )}

      {/* Gallery preview carousel (groups) */}
      <CreativePreviewModal
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        images={previewImages}
        startIndex={previewStartIndex}
      />
    </div>
  );
};

export default CreativeTable;
